import type { ReactNode } from 'react';
import { cx } from '../cx';
import { WIDE_ONLY, type ChartLayout } from './layout';
import { LABEL_TEXT, VALUE_TEXT } from './scale';

export type ButterflyChartProps = {
  /** The two sides ("Helsinki", "Tampere"): left in ink, right in iris (two equals; sun stays free). */
  sides: [ReactNode, ReactNode];
  /** One row per category with each side's value, formatted. */
  rows: Array<{
    id: string;
    label: ReactNode;
    left: number;
    right: number;
    leftDisplay: ReactNode;
    rightDisplay: ReactNode;
  }>;
  /** The shared maximum; defaults to the largest value on either side. */
  max?: number;
  /** `auto` (default) switches to the narrow form in a narrow container: the category label centred above its row, the half-bars full width. */
  layout?: ChartLayout;
  /** One sentence that states the finding, for screen readers. */
  description: string;
  className?: string;
};

const HEAD: Record<ChartLayout, string> = { auto: 'gap-2 @md:gap-0', narrow: 'gap-2', wide: '' };
// The narrow row is a two-column grid: the label spans both columns on top, the halves sit beneath it.
const ROW: Record<ChartLayout, string> = {
  auto: 'grid grid-cols-2 gap-y-0.5 @md:flex @md:items-center',
  narrow: 'grid grid-cols-2 gap-y-0.5',
  wide: 'flex items-center',
};
const ROW_LABEL: Record<ChartLayout, string> = {
  auto: 'order-first col-span-2 @md:order-none @md:col-auto @md:w-28 @md:shrink-0 @md:px-2',
  narrow: 'order-first col-span-2',
  wide: 'w-28 shrink-0 px-2',
};

/**
 * Two places or groups compared category by category, back to back on one shared scale: Helsinki
 * against Tampere by field. Use shares, not counts, when the two differ a lot in size.
 */
export function ButterflyChart({ sides, rows, max, layout = 'auto', description, className }: ButterflyChartProps) {
  const top = max ?? Math.max(1, ...rows.flatMap((r) => [r.left, r.right]));
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-2', className)}>
      <div className={cx('flex text-sm font-semibold text-jc-ink', HEAD[layout])} aria-hidden="true">
        <span className="flex-1 text-right">{sides[0]}</span>
        <span className={cx('w-28 shrink-0', WIDE_ONLY[layout])} />
        <span className="flex-1">{sides[1]}</span>
      </div>
      {rows.map((r) => (
        <div key={r.id} className={ROW[layout]} aria-hidden="true">
          <span className="flex min-w-0 flex-1 items-center justify-end gap-2">
            <span className={VALUE_TEXT}>{r.leftDisplay}</span>
            <span
              className="block h-3.5 origin-right rounded-l-jc-info bg-jc-chart-1 motion-safe:animate-jc-grow-x"
              style={{ width: `calc((100% - 3.5rem) * ${r.left / top})` }}
            />
          </span>
          <span className={cx('truncate text-center', LABEL_TEXT, ROW_LABEL[layout])}>{r.label}</span>
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span
              className="block h-3.5 origin-left rounded-r-jc-info bg-jc-chart-3 motion-safe:animate-jc-grow-x"
              style={{ width: `calc((100% - 3.5rem) * ${r.right / top})` }}
            />
            <span className={VALUE_TEXT}>{r.rightDisplay}</span>
          </span>
        </div>
      ))}
    </div>
  );
}
